import React, { useEffect, useState } from 'react';
import {
  Typography,
  Card,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Alert,
  Divider,
} from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createOrder, clearError, clearSuccess } from '../store/slices/ordersSlice';
import regionService from '../services/regionService';
import { formatPrice } from '../utils/formatters';
import Loader from '../components/common/Loader';
import useTelegram from '../hooks/useTelegram';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const cartItems = useSelector((state) => state.cart.items);
  const cartTotal = useSelector((state) => state.cart.total);
  const { isAuthenticated, loading: authLoading } = useSelector((state) => state.auth);
  const { loading, error, success } = useSelector((state) => state.orders);
  const { showMainButton, hideMainButton, showAlert } = useTelegram();

  const [regions, setRegions] = useState([]);

  useEffect(() => {
    // Load regions
    const loadRegions = async () => {
      try {
        const response = await regionService.getAll();
        setRegions(response.regions || []);
      } catch (error) {
        console.error('Failed to load regions:', error);
      }
    };
    loadRegions();
  }, []);

  useEffect(() => {
    // Redirect if not authenticated
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: location.pathname } });
    }
  }, [isAuthenticated, authLoading, navigate, location]);

  useEffect(() => {
    // Redirect if cart is empty, but NOT if we just successfully placed an order
    if (cartItems.length === 0 && !success) {
      navigate('/cart');
    }
  }, [cartItems, navigate, success]);

  useEffect(() => {
    const handleSubmit = () => form.submit();
    // Show Telegram main button
    showMainButton('Buyurtma berish', handleSubmit);
    return () => hideMainButton(handleSubmit);
  }, [form]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Handle order creation success
    if (success) {
      if (showAlert) showAlert('Buyurtma muvaffaqiyatli yaratildi!');

      // Delay navigation to show success state
      const timer = setTimeout(() => {
        dispatch(clearSuccess());
        navigate('/orders');
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [success, dispatch, navigate, showAlert]);

  const onFinish = (values) => {
    const orderData = {
      region_id: parseInt(values.region_id),
      delivery_address: values.delivery_address,
      customer_note: values.customer_note,
      items: cartItems.map((item) => ({
        product_id: item.id,
        quantity: item.quantity,
      })),
    };

    dispatch(createOrder(orderData));
  };

  const onFinishFailed = (errorInfo) => {
    if (showAlert) showAlert('Iltimos, barcha kerakli maydonlarni to\'ldiring');
  };

  if (loading) {
    return <Loader overlay text="Buyurtma yaratilmoqda..." />;
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 20px' }}>
      <Title level={2} style={{ marginBottom: 24, color: '#f8fafc', fontWeight: 800, fontFamily: 'Outfit' }}>
        Buyurtmani rasmiylashtirish
      </Title>

      {error && (
        <Alert
          message="Xatolik"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 24 }}
          onClose={() => dispatch(clearError())}
        />
      )}

      {success && (
        <Alert
          message="Muvaffaqiyatli"
          description={success}
          type="success"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      <Row gutter={[24, 24]}>
        <Col xs={24} md={16}>
          <Card title="Yetkazib berish ma'lumotlari">
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
            >
              <Form.Item
                label="Viloyat"
                name="region_id"
                rules={[{ required: true, message: 'Iltimos, viloyatni tanlang' }]}
              >
                <Select placeholder="Viloyatni tanlang">
                  {regions.map((region) => (
                    <Option key={region.id} value={region.id}>
                      {region.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Yetkazib berish manzili"
                name="delivery_address"
                rules={[{ required: true, message: 'Iltimos, manzilni kiriting' }]}
              >
                <TextArea
                  rows={3}
                  placeholder="Toshkent shahri, Chilonzor tumani, 12-mavze, 5-uy"
                />
              </Form.Item>

              <Form.Item
                label="Izoh (ixtiyoriy)"
                name="customer_note"
              >
                <TextArea
                  rows={2}
                  placeholder="Qo'shimcha izohlaringiz"
                />
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card title="Buyurtma tafsilotlari">
            {cartItems.map((item) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                }}
              >
                <Text>
                  {item.name} x {item.quantity}
                </Text>
                <Text>
                  {formatPrice(
                    parseFloat(item.discount_price || item.price) * item.quantity
                  )}
                </Text>
              </div>
            ))}

            <Divider style={{ margin: '16px 0' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
              <Title level={5}>Jami:</Title>
              <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                {formatPrice(cartTotal)}
              </Title>
            </div>

            <Button
              type="primary"
              block
              size="large"
              onClick={() => form.submit()}
              disabled={loading}
              loading={loading}
            >
              Buyurtma berish
            </Button>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CheckoutPage;
